import envConfig, { isEnvFeatureEnabled } from '@app/enviroment/enviroment';
import AutoScheduleRoutes from '@app/routes/AutoScheduleRoutes';
import AvailabilityRoutes from '@app/routes/AvailabilityRoutes';
import CaregiverGroupsRoutes from '@app/routes/CaregiverGroupsRoutes';
import CaregiverRoutes from '@app/routes/CaregiverRoutes';
import ClientCategoriesRoutes from '@app/routes/ClientCategoriesRoutes';
import InventoryCategoriesRoutes from '@app/routes/InventoryCategoriesRoutes';
import ClientRoutes from '@app/routes/ClientRoutes';
import DashboardRoutes from '@app/routes/DashboardRoutes';
import DoctorRoutes from '@app/routes/DoctorRoutes';
import HistoricRoutesRoutes from '@app/routes/HistoricRoutesRoutes';
import NotificationsRoutes from '@app/routes/NotificationsRoutes';
import QueryFiltersRoutes from '@app/routes/QueryFiltersRoutes';
import RouteKmReportRoutes from '@app/routes/RouteKmReportRoutes';
import SchedulingRoutes from '@app/routes/SchedulingRoutes';
import ServiceRequestsRoutes from '@app/routes/ServiceRequestsRoutes';
import ServicePackageRoutes from '@app/routes/ServicePackageRoutes';
import ServiceRoutes from '@app/routes/ServiceRoutes';
import ServiceTypesRoutes from '@app/routes/ServiceTypesRoutes';
import ShiftsRoutes from '@app/routes/ShiftsRoutes';
import VisitRoutes from '@app/routes/VisitRoutes';
import { MenuItem, MenuStructure, NestedRoutes, Route, RoutesStructure } from '@app/types/routing.types';
import { addFullPathToRoutes } from './addFullPathsRoutes';

import {
  AUTO_SCHEDULE_PAGE,
  AVAILABILITY_PAGE,
  BALANCES_PAGE,
  BILLING_OVERVIEW_PAGE,
  CAREGIVER_CASH_PAGE,
  CAREGIVER_GROUP_PAGE,
  CAREGIVERS_LIST,
  CAREGIVERS_PREFIX,
  CLIENT_CATEGORY_PAGE,
  CLIENT_PAGE,
  CLIENTS_LIST,
  CUSTOMER_PROFILE_PAGE,
  DASHBOARD_PAGE,
  DOCTOR_LIST,
  DOCTOR_PAGE,
  DOCTOR_PORTAL_MEASUREMENTS,
  DOCTOR_PORTAL_OVERVIEW,
  DOCTOR_PORTAL_PAGE,
  DOCTOR_PORTAL_PATIENT_CARD,
  DOCTOR_PORTAL_PATIENTS,
  DOCTOR_PORTAL_PROFILE,
  DOCTOR_PORTAL_VISITS,
  DOCTOR_INSTRUCTION_QUEUE_PAGE,
  EDIT_CAREGIVER,
  EDIT_CLIENT,
  EDIT_DOCTOR,
  EDIT_SERVICE,
  EDIT_SERVICE_PACKAGE,
  EDIT_SERVICE_REQUEST_ARRANGEMENT,
  EDIT_USER,
  EDIT_VISIT,
  HISTORIC_ROUTES_PAGE,
  INVENTORY_CATEGORY_PAGE,
  INVENTORY_PAGE,
  NEW_CAREGIVER,
  NEW_CLIENT,
  NEW_DOCTOR,
  NEW_SERVICE,
  NEW_SERVICE_PACKAGE,
  NEW_USER,
  NOTIFICATIONS_LIST,
  NOTIFICATIONS_PAGE,
  PAYMENTS_PAGE,
  PAYROLL_LIST,
  PAYROLL_PAGE,
  PAYROLL_RUN_DETAIL,
  QUERY_FILTERS_TEST_PAGE,
  ROUTE_KM_REPORT_PAGE,
  SCHEDULING_PAGE,
  SERVICE_LIST,
  SERVICE_PACKAGE_LIST,
  SERVICE_PACKAGE_PAGE,
  SERVICE_PAGE,
  SERVICE_REQUESTS_MANAGEMENT_PAGE,
  SERVICE_REQUESTS_MANAGEMENT_PAGE_LIST,
  SERVICE_REQUESTS_PAGE,
  SERVICE_REQUEST_ARRANGEMENT_PAGE,
  SERVICE_REQUEST_ARRANGEMENT_INITIAL,
  SERVICE_REQUEST_ARRANGEMENT_COMPOSE,
  SERVICE_TYPE_PAGE,
  SHIFTS_PAGE,
  USER_AUDIT_LOGS,
  USER_AUDIT_TRAIL,
  USERS_ONLINE,
  USERS_LIST,
  USERS_PREFIX,
  VIEW_CLIENT,
  VISITS_CALENDAR_PAGE,
  VISITS_LIST,
  VISITS_PAGE,
  VISIT_SUMMARIES_LIST,
  FORCED_ASSIGNMENTS_LIST,
} from '@app/routes/urls';

import BalancesRoutes from '@app/routes/BalancesRoutes';
import CaregiverCashRoutes from '@app/routes/CaregiverCashRoutes';
import BillingRoutes from '@app/routes/BillingOverviewRoutes';
import CustomerProfileRoutes from '@app/routes/CustomerProfileRoutes';
import DoctorPortalRoutes from '@app/routes/DoctorPortalRoutes';
import DoctorInstructionsRoutes from '@app/routes/DoctorInstructionsRoutes';
import InventoryRoutes from '@app/routes/InventoryRoutes';
import PaymentsRoutes from '@app/routes/PaymentsRoutes';
import PayrollRoutes from '@app/routes/PayrollRoutes';
import UserRoutes from '@app/routes/UserRoutes';
import VisitsCalendarRoutes from '@app/routes/VisitsCalendarRoutes';

import { restrictToDoctorPortal } from './doctorPortalRoutes';

const Caregivers = {
  [CAREGIVERS_PREFIX]: {
    full_path: '',
    show: true,
    routes: {
      [NEW_CAREGIVER]: {
        full_path: '',
        route: CaregiverRoutes.CaregiverFormPage,
        show: true,
      },
      [CAREGIVERS_LIST]: {
        full_path: '',
        route: CaregiverRoutes.CaregiverTablePage,
        show: true,
      },
      [EDIT_CAREGIVER]: {
        full_path: '',
        route: CaregiverRoutes.CaregiverEditPage,
        show: true,
      },
    },
  },
} as const;

const Users = {
  [USERS_PREFIX]: {
    full_path: '',
    show: true,
    routes: {
      [USERS_LIST]: {
        full_path: '',
        route: UserRoutes.UsersTablePage,
        show: true,
      },
      [NEW_USER]: {
        full_path: '',
        route: UserRoutes.UserCreatePage,
        show: true,
      },
      [EDIT_USER]: {
        full_path: '',
        route: UserRoutes.UserEditPage,
        show: true,
      },
      [USER_AUDIT_LOGS]: {
        full_path: '',
        route: UserRoutes.UserAuditLogsPage,
        show: true,
      },
      [USER_AUDIT_TRAIL]: {
        full_path: '',
        route: UserRoutes.UserAuditTrailPage,
        show: true,
      },
      [USERS_ONLINE]: {
        full_path: '',
        route: UserRoutes.UsersOnlinePage,
        show: true,
      },
    },
  },
} as const;

// `canViewBilling` gates the billing module (overview, ledgers, payments,
// balances). Roles such as Office Administrator ('ofa') get full admin access
// but no billing, so these routes are hidden from both the menu and the router.
// `canViewVisitSummaries` gates the visit summary reports list, which calls an
// admin-only backend endpoint — non-admin roles never see the route or menu entry.
// `canReviewDoctorInstructions` gates the doctor-instruction queue. Its endpoint is
// guarded with require_realm_roles(["adm"]), which accepts the admin-inheriting
// roles too, so an Office Administrator ('ofa') may decide an instruction and sees it.
// Whether the doctor's threshold-alerting surfaces are shown: the Measurements
// screen, and the overview's out-of-range tile and attention panel that read the
// same breaches. Off — a doctor setting alerting bounds nothing acts on is a
// promise the product does not keep. Vitals themselves are unaffected; they are a
// measurement, not an alert, and still show on every visit.
export const DOCTOR_THRESHOLD_ALERTS_SHOWN = false;

export const generateRouteStructure = (
  canViewBilling: boolean = true,
  canViewVisitSummaries: boolean = true,
  canManageInventory: boolean = true,
  isDoctor: boolean = false,
  canReviewDoctorInstructions: boolean = true
): RoutesStructure => {
  const obj: RoutesStructure = {
    [DASHBOARD_PAGE]: {
      full_path: '',
      show: true,
      route: DashboardRoutes.DashboardPage,
    },
    ...Caregivers,
    ...Users,
    [CLIENT_PAGE]: {
      full_path: '',
      show: true,
      routes: {
        [NEW_CLIENT]: {
          full_path: '',
          route: ClientRoutes.ClientFormPage,
          show: true,
        },
        [CLIENTS_LIST]: {
          full_path: '',
          route: ClientRoutes.ClientTablePage,
          show: true,
        },
        [EDIT_CLIENT]: {
          full_path: '',
          route: ClientRoutes.ClientEditPage,
          show: true,
        },
        [VIEW_CLIENT]: {
          full_path: '',
          route: ClientRoutes.ClientViewPage,
          show: true,
        },
      },
    },
    [DOCTOR_PAGE]: {
      full_path: '',
      show: true,
      routes: {
        [NEW_DOCTOR]: {
          full_path: '',
          route: DoctorRoutes.DoctorFormPage,
          show: true,
        },
        [DOCTOR_LIST]: {
          full_path: '',
          route: DoctorRoutes.DoctorTablePage,
          show: true,
        },
        [EDIT_DOCTOR]: {
          full_path: '',
          route: DoctorRoutes.DoctorEditPage,
          show: true,
        },
      },
    },
    [SERVICE_PAGE]: {
      full_path: '',
      show: true,
      routes: {
        [NEW_SERVICE]: {
          full_path: '',
          route: ServiceRoutes.ServiceFormPage,
          show: true,
        },
        [SERVICE_LIST]: {
          full_path: '',
          route: ServiceRoutes.ServiceTablePage,
          show: true,
        },
        [EDIT_SERVICE]: {
          full_path: '',
          route: ServiceRoutes.ServiceEditPage,
          show: true,
        },
      },
    },
    [SERVICE_TYPE_PAGE]: {
      full_path: '',
      show: true,
      route: ServiceTypesRoutes.ServiceTypeTablePage,
    },
    [SERVICE_PACKAGE_PAGE]: {
      full_path: '',
      show: true,
      routes: {
        // The list comes first on purpose: RoutingTransformer redirects the
        // section's index to its first visible child.
        [SERVICE_PACKAGE_LIST]: {
          full_path: '',
          route: ServicePackageRoutes.ServicePackageTablePage,
          show: true,
        },
        [NEW_SERVICE_PACKAGE]: {
          full_path: '',
          route: ServicePackageRoutes.ServicePackageFormPage,
          show: true,
        },
        [EDIT_SERVICE_PACKAGE]: {
          full_path: '',
          route: ServicePackageRoutes.ServicePackageEditPage,
          show: true,
        },
      },
    },
    [CLIENT_CATEGORY_PAGE]: {
      full_path: '',
      show: true,
      route: ClientCategoriesRoutes.ClientCategoryTablePage,
    },
    [INVENTORY_CATEGORY_PAGE]: {
      full_path: '',
      show: isEnvFeatureEnabled('INVENTORY_ENABLED'),
      route: InventoryCategoriesRoutes.InventoryCategoryTablePage,
    },
    [CAREGIVER_GROUP_PAGE]: {
      full_path: '',
      show: true,
      route: CaregiverGroupsRoutes.CaregiverGroupTablePage,
    },
    // TODO: delete this route and ServiceRequestsRoutes.ServiceRequestsPage once the
    // arrangement authoring flow below has fully replaced it. Hidden from the sidebar
    // (show: false) but left reachable so existing links/imports keep working meanwhile.
    [SERVICE_REQUESTS_PAGE]: {
      full_path: '',
      show: false,
      route: ServiceRequestsRoutes.ServiceRequestsPage,
    },
    [SERVICE_REQUEST_ARRANGEMENT_PAGE]: {
      full_path: '',
      show: true,
      routes: {
        // Listed first: RoutingTransformer redirects the section's index to its first
        // visible child, and a fresh visit to this section belongs at step 0.
        [SERVICE_REQUEST_ARRANGEMENT_INITIAL]: {
          full_path: '',
          route: ServiceRequestsRoutes.ServiceRequestArrangementPage,
          show: true,
        },
        [SERVICE_REQUEST_ARRANGEMENT_COMPOSE]: {
          full_path: '',
          route: ServiceRequestsRoutes.ServiceRequestArrangementPage,
          show: true,
        },
      },
    },
    [AVAILABILITY_PAGE]: {
      full_path: '',
      show: true,
      route: AvailabilityRoutes.AvailabilityPage,
    },
    [SHIFTS_PAGE]: {
      full_path: '',
      show: true,
      route: ShiftsRoutes.ShiftsPage,
    },
    [SCHEDULING_PAGE]: {
      full_path: '',
      show: true,
      route: SchedulingRoutes.SchedulingRouteContainer,
    },
    [AUTO_SCHEDULE_PAGE]: {
      full_path: '',
      show: envConfig.getEnvKey('AUTOSCHEDULE_ENABLED') === true,
      route: AutoScheduleRoutes.AutoScheduleRouteContainer,
    },
    [HISTORIC_ROUTES_PAGE]: {
      full_path: '',
      show: true,
      route: HistoricRoutesRoutes.HistoricRoutesRouteContainer,
    },
    [ROUTE_KM_REPORT_PAGE]: {
      full_path: '',
      show: isEnvFeatureEnabled('ROUTE_DISTANCES_ENABLED'),
      route: RouteKmReportRoutes.RouteKmReportRouteContainer,
    },
    [VISITS_PAGE]: {
      full_path: '',
      show: true,
      routes: {
        [VISITS_LIST]: {
          full_path: '',
          route: VisitRoutes.VisitTablePage,
          show: true,
        },
        [EDIT_VISIT]: {
          full_path: '',
          route: VisitRoutes.VisitEditPage,
          show: true,
        },
        [VISIT_SUMMARIES_LIST]: {
          full_path: '',
          route: VisitRoutes.VisitSummariesTablePage,
          show: canViewVisitSummaries,
        },
        // Same admin-only backend guard as the summaries list.
        [FORCED_ASSIGNMENTS_LIST]: {
          full_path: '',
          route: VisitRoutes.ForcedAssignmentsTablePage,
          show: canViewVisitSummaries,
        },
      },
    },
    [SERVICE_REQUESTS_MANAGEMENT_PAGE]: {
      full_path: '',
      show: true,
      routes: {
        [SERVICE_REQUESTS_MANAGEMENT_PAGE_LIST]: {
          full_path: '',
          route: ServiceRequestsRoutes.ServiceRequestsTablePage,
          show: true,
        },
        // Editing one request's lines. Same `:id/edit` pattern as EDIT_VISIT — routed,
        // never in the menu (getMenu only references the list).
        [EDIT_SERVICE_REQUEST_ARRANGEMENT]: {
          full_path: '',
          route: ServiceRequestsRoutes.ServiceRequestArrangementEditPage,
          show: true,
        },
      },
    },
    [NOTIFICATIONS_PAGE]: {
      full_path: '',
      show: true,
      routes: {
        [NOTIFICATIONS_LIST]: {
          full_path: '',
          route: NotificationsRoutes.NotificationsList,
          show: true,
        },
      },
    },
    [VISITS_CALENDAR_PAGE]: {
      full_path: '',
      show: true,
      route: VisitsCalendarRoutes.VisitsCalendarRoute,
    },
    [DOCTOR_INSTRUCTION_QUEUE_PAGE]: {
      full_path: '',
      show: canReviewDoctorInstructions,
      route: DoctorInstructionsRoutes.DoctorInstructionQueueRoute,
    },
    [QUERY_FILTERS_TEST_PAGE]: {
      full_path: '',
      show: true,
      route: QueryFiltersRoutes.QueryFiltersTestPage,
    },
    [BILLING_OVERVIEW_PAGE]: {
      full_path: '',
      show: canViewBilling,
      route: BillingRoutes.BillingOverviewRoute,
    },
    [BALANCES_PAGE]: {
      full_path: '',
      show: canViewBilling,
      route: BalancesRoutes.BalancesRoute,
    },
    [CAREGIVER_CASH_PAGE]: {
      full_path: '',
      // Env-flagged (off by default) and part of the admin-only billing module.
      show: canViewBilling && envConfig.getEnvKey('CAREGIVER_CASH_ENABLED') === true,
      route: CaregiverCashRoutes.CaregiverCashRoute,
    },
    [PAYMENTS_PAGE]: {
      full_path: '',
      show: canViewBilling,
      route: PaymentsRoutes.PaymentsRoute,
    },
    [CUSTOMER_PROFILE_PAGE]: {
      full_path: '',
      show: canViewBilling,
      route: CustomerProfileRoutes.CustomerProfilePage,
    },
    [PAYROLL_PAGE]: {
      full_path: '',
      show: canViewBilling && isEnvFeatureEnabled('PAYROLL_ENABLED'),
      routes: {
        [PAYROLL_LIST]: {
          full_path: '',
          route: PayrollRoutes.PayrollRunsListRoute,
          show: canViewBilling && isEnvFeatureEnabled('PAYROLL_ENABLED'),
        },
        [PAYROLL_RUN_DETAIL]: {
          full_path: '',
          route: PayrollRoutes.PayrollRunDetailRoute,
          show: canViewBilling && isEnvFeatureEnabled('PAYROLL_ENABLED'),
        },
      },
    },
    [INVENTORY_PAGE]: {
      full_path: '',
      show: canManageInventory && isEnvFeatureEnabled('INVENTORY_ENABLED'),
      route: InventoryRoutes.InventoryRoute,
    },
    // The doctor's own screens. Hidden from staff, and the only thing a doctor
    // sees — restrictToDoctorPortal flips both halves of that in one place.
    [DOCTOR_PORTAL_PAGE]: {
      full_path: '',
      show: isDoctor,
      routes: {
        // First, and so the doctor's landing page: getIndexRouteKey takes the
        // first visible menu entry, and the menu lists these in this order.
        [DOCTOR_PORTAL_OVERVIEW]: {
          full_path: '',
          route: DoctorPortalRoutes.DoctorOverviewRoute,
          show: isDoctor,
        },
        [DOCTOR_PORTAL_PATIENTS]: {
          full_path: '',
          route: DoctorPortalRoutes.DoctorPatientsRoute,
          show: isDoctor,
        },
        // Reached from the patients list, not from the sidebar — it needs a
        // patient. getMenu lists no entry for it, and a route with no menu entry
        // simply has no sidebar item; `show` still gates whether it resolves.
        [DOCTOR_PORTAL_PATIENT_CARD]: {
          full_path: '',
          route: DoctorPortalRoutes.DoctorPatientCardRoute,
          show: isDoctor,
        },
        [DOCTOR_PORTAL_VISITS]: {
          full_path: '',
          route: DoctorPortalRoutes.DoctorVisitsRoute,
          show: isDoctor,
        },
        [DOCTOR_PORTAL_MEASUREMENTS]: {
          full_path: '',
          route: DoctorPortalRoutes.DoctorMeasurementsRoute,
          // The threshold-alerting screen: set a bound per vital, get a list of
          // the measurements that crossed it. The product does not do that, so
          // it offers a doctor a setting that drives nothing. `show: false`
          // rather than a deletion because the page, its endpoints and its RLS
          // all work — this is the one line that brings it back.
          show: DOCTOR_THRESHOLD_ALERTS_SHOWN && isDoctor,
        },
        [DOCTOR_PORTAL_PROFILE]: {
          full_path: '',
          route: DoctorPortalRoutes.DoctorProfileRoute,
          show: isDoctor,
        },
      },
    },
  };

  addFullPathToRoutes(obj);
  // Hide everything else rather than enumerating what to hide: a page added
  // later then defaults to invisible for the doctor instead of leaking in.
  return isDoctor ? restrictToDoctorPortal(obj) : obj;
};

export const getAllPaths = (routes: RoutesStructure | NestedRoutes): string[] => {
  let paths: string[] = [];
  Object.values(routes).forEach((route: Route) => {
    if (route.full_path && route.show) paths.push(route.full_path);
    if ('routes' in route && route.routes) {
      paths = paths.concat(getAllPaths(route.routes));
    }
  });
  return paths;
};

export const getIndexRouteKey = (menu: MenuStructure, hasPermissions: boolean): string | null => {
  if (!hasPermissions || menu.length === 0) return null;
  const firstMenuItem = menu[0];
  return firstMenuItem?.children?.[0]?.key ?? firstMenuItem?.key ?? null;
};

export const getParentKey = (menu: MenuStructure, key: string): string | null => {
  const struct: Record<string, string> = {};
  menu.forEach((item: MenuItem) => {
    item.children?.forEach((child: MenuItem) => {
      struct[child.key] = item.key;
    });
  });
  return struct[key] || null;
};
